import axiosInstance from './axiosInstance';
import axios, { AxiosRequestConfig, Method } from 'axios';
import {
  API_AUTH_LOGIN_URL,
  API_AUTH_LOGOUT_URL,
  API_AUTH_REFRESH_TOKEN_URL,
  API_FORGOT_PASSWORD,
  API_METHOD_POST,
  API_REGISTER_URL,
  API_RESET_PASSWORD,
} from '@/constants/apiConstants';
import { toast } from 'react-toastify';
import { refreshLock } from '@/utility/api/tokenRefreshLock';

/** Auth workflow paths that must never trigger automatic token refresh on 401. */
const AUTH_REFRESH_EXCLUDED_SEGMENTS = [
  '/auth/login',
  '/auth/social-login',
  '/auth/refresh-token',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/logout',
  '/onboarding-team-members',
] as const;

const resolveAbsoluteRequestPath = (url: string): string => {
  const withoutQuery = url.split('?')[0];

  if (/^https?:\/\//i.test(withoutQuery)) {
    return new URL(withoutQuery).pathname.replace(/\/+$/, '');
  }

  const baseURL = (axiosInstance.defaults.baseURL || '').replace(/\/$/, '');

  if (withoutQuery.startsWith('/v1/') || withoutQuery.startsWith('/v2/')) {
    const apiPrefix = baseURL.endsWith('/api') ? '/api' : '';
    return `${apiPrefix}${withoutQuery}`.replace(/\/+$/, '');
  }

  if (withoutQuery.startsWith('/api/')) {
    return withoutQuery.replace(/\/+$/, '');
  }

  try {
    return new URL(
      withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`,
      `${baseURL}/`,
    ).pathname.replace(/\/+$/, '');
  } catch {
    return withoutQuery.replace(/\/+$/, '');
  }
};

const REFRESH_EXCLUDED_ABSOLUTE_PATHS = new Set(
  [
    API_AUTH_LOGIN_URL,
    API_AUTH_LOGOUT_URL,
    API_AUTH_REFRESH_TOKEN_URL,
    API_FORGOT_PASSWORD,
    API_RESET_PASSWORD,
    API_REGISTER_URL,
    `${axiosInstance.defaults.baseURL || ''}/v1/auth/social-login`,
    `${axiosInstance.defaults.baseURL || ''}/v2/auth/social-login`,
  ].map(resolveAbsoluteRequestPath),
);

const isRefreshExcludedRequest = (url: string): boolean => {
  const absolutePath = resolveAbsoluteRequestPath(url);

  if (REFRESH_EXCLUDED_ABSOLUTE_PATHS.has(absolutePath)) {
    return true;
  }

  return AUTH_REFRESH_EXCLUDED_SEGMENTS.some((segment) =>
    absolutePath.includes(segment),
  );
};

interface ApiCallOptions {
  method: Method;
  url: string;
  data?: any;
  config?: AxiosRequestConfig;
  headers?: Record<string, string>;
  token?: string;
  refresh_token?: string;
  onUploadProgress?: (progressEvent: any) => void;
  suppressErrorToast?: boolean;
}

const processQueue = (error: unknown | null, token: string | null = null) => {
  refreshLock.failedQueue.forEach((queuedRequest) => {
    if (error || !token) {
      queuedRequest.reject(error ?? new Error('Token refresh failed'));
      return;
    }

    queuedRequest.resolve(token);
  });

  refreshLock.failedQueue = [];
};

const refreshAccessToken = async (token: string) => {
  try {
    const response = await axiosInstance({
      method: API_METHOD_POST,
      url: API_AUTH_REFRESH_TOKEN_URL,
      data: '',
      headers: {
        ...axiosInstance.defaults.headers.common,
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    const newAccessToken = response.data?.data?.access_token?.token;
    const newRefreshToken = response.data?.data?.refresh_token?.token;

    if (typeof window !== 'undefined') {
      localStorage.setItem('ACCESS_TOKEN', newAccessToken);
      localStorage.setItem('REFRESH_TOKEN', newRefreshToken);
    }

    return newAccessToken;
  } catch (error) {
    if (typeof window !== 'undefined') {
      toast.error('Your session has expired. Please log in again to continue.');

      const { purgeSession } = await import('@/utility/auth/purgeSession');
      await purgeSession({ redirectToLogin: true });
    }

    console.error('Token refresh error:', error);
    return null;
  }
};

const handlePaymentRequiredRedirect = () => {
  if (
    typeof window !== 'undefined' &&
    window.location.pathname !== '/subscription'
  ) {
    window.location.href = '/subscription';
  }
};

const handleForbiddenRedirect = (error: any) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (
    window.location.pathname === '/403' ||
    window.location.search.includes('terms-acceptance=true')
  ) {
    return;
  }

  if (error.response?.data?.code === 'TERMS_ACCEPTANCE_REQUIRED') {
    window.location.href = '/?terms-acceptance=true';
    return;
  }

  window.location.href = '/403';
};

const retryOriginalRequest = (
  originalRequest: Omit<ApiCallOptions, 'token'>,
  token: string,
) =>
  apiCall({
    ...originalRequest,
    token,
  });

const enqueueRequestWhileRefreshing = (
  originalRequest: Omit<ApiCallOptions, 'token'>,
) =>
  new Promise((resolve, reject) => {
    refreshLock.failedQueue.push({
      resolve: (token: string) => {
        resolve(retryOriginalRequest(originalRequest, token));
      },
      reject,
    });
  });

const handleUnauthorizedRequest = async (
  originalRequest: Omit<ApiCallOptions, 'token'>,
  error: unknown,
) => {
  const refreshToken =
    typeof window !== 'undefined' ? localStorage.getItem('REFRESH_TOKEN') : null;

  if (!refreshToken) {
    throw error;
  }

  if (refreshLock.isRefreshing) {
    return enqueueRequestWhileRefreshing(originalRequest);
  }

  refreshLock.isRefreshing = true;

  try {
    const newAccessToken = await refreshAccessToken(refreshToken);

    if (!newAccessToken) {
      const refreshError = new Error('Token refresh failed');
      processQueue(refreshError, null);
      throw refreshError;
    }

    processQueue(null, newAccessToken);

    return retryOriginalRequest(originalRequest, newAccessToken);
  } catch (refreshError) {
    processQueue(refreshError, null);
    throw refreshError;
  } finally {
    refreshLock.isRefreshing = false;
  }
};

export const apiCall = async ({
  method,
  url,
  data = null,
  config = {},
  headers = {},
  token: overrideToken,
  onUploadProgress,
  suppressErrorToast = false,
}: ApiCallOptions): Promise<any> => {
  try {
    const token =
      overrideToken ??
      (typeof window !== 'undefined' ? localStorage.getItem('ACCESS_TOKEN') : null);

    const response = await axiosInstance({
      method,
      url,
      data,
      headers: {
        ...axiosInstance.defaults.headers.common,
        ...headers,
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      ...(onUploadProgress && { onUploadProgress }),
      ...config,
    });

    return response.data;
  } catch (error: any) {
    if (axios.isCancel(error)) {
      throw error;
    }

    const originalRequest = {
      method,
      url,
      data,
      config,
      headers,
      onUploadProgress,
      suppressErrorToast,
    };

    if (axios.isAxiosError(error)) {
      const status = error.response?.status;

      if (status === 402) {
        handlePaymentRequiredRedirect();
        throw error;
      }

      if (status === 403) {
        handleForbiddenRedirect(error);
        throw error;
      }

      if (status === 401 && !isRefreshExcludedRequest(url)) {
        return handleUnauthorizedRequest(originalRequest, error);
      }
    }

    if (error) {
      const errorMessage = error?.response?.data?.message;
      const errorStatus = error?.response?.status;
      const requestUrl = error?.config?.url;

      if (
        errorMessage === 'Price book not found for this supplier' ||
        (errorStatus === 404 &&
          requestUrl &&
          requestUrl.includes('/price-book/latest'))
      ) {
        // Skip toast for expected not-found responses.
      } else if (!suppressErrorToast) {
        toast.error(errorMessage || error?.message || 'Something went wrong');
      }

      throw error;
    }

    console.error('Unexpected error');
    console.error('An unexpected error occurred. Please try again.');
  }
};

export const isRequestCanceled = (error: unknown): boolean =>
  axios.isCancel(error);
